import { useState, useEffect } from "react";
import axios from "axios";
import { Building2, Search, X, Plus, ArrowLeft, Globe, Users, Package, Shield } from "lucide-react";
import CompareInsights from "../common/CompareInsights";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const OC = { care: "#10b981", utility: "#3b82f6", creative: "#a855f7", extraction: "#ef4444" };
const OC_LABELS = { care: "Care", utility: "Utility", creative: "Creative", extraction: "Extraction" };
const DIM_COLORS = ["#f59e0b", "#10b981", "#3b82f6", "#a855f7"];

// Popular AI companies for one-click comparison (no typing needed)
const QUICK_PICK_COMPANIES = [
  { domain: "openai.com", name: "OpenAI" },
  { domain: "anthropic.com", name: "Anthropic" },
  { domain: "google.com", name: "Google" },
  { domain: "deepmind.google", name: "DeepMind" },
  { domain: "microsoft.com", name: "Microsoft" },
  { domain: "meta.com", name: "Meta" },
  { domain: "x.ai", name: "xAI" },
  { domain: "mistral.ai", name: "Mistral" },
  { domain: "cohere.com", name: "Cohere" },
  { domain: "huggingface.co", name: "Hugging Face" },
];

export const CompanyCompare = ({ initialDomains = [], onBack, onViewDetail }) => {
  const [companies, setCompanies] = useState([]);
  const [missing, setMissing] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [selectedDomains, setSelectedDomains] = useState(initialDomains);

  useEffect(() => {
    if (selectedDomains.length === 0) { setCompanies([]); setMissing([]); return; }
    setLoading(true);
    axios.get(`${API}/companies/compare`, { params: { domains: selectedDomains.join(",") }, timeout: 30000 })
      .then(r => {
        setCompanies(r.data.companies || []);
        setMissing(r.data.missing || []);
      })
      .catch(() => { setCompanies([]); setMissing([]); })
      .finally(() => setLoading(false));
  }, [selectedDomains]);

  const handleSearch = async (q) => {
    setSearchQuery(q);
    if (q.length < 2) { setSearchResults(null); return; }
    try {
      const res = await axios.get(`${API}/companies/search`, { params: { q } });
      setSearchResults((res.data.results || []).filter(c => !selectedDomains.includes(c.domain)));
    } catch { setSearchResults(null); }
  };

  const addCompany = (domain) => {
    if (selectedDomains.length >= 4 || selectedDomains.includes(domain)) return;
    setSelectedDomains([...selectedDomains, domain]);
    setSearchQuery("");
    setSearchResults(null);
  };

  const removeCompany = (domain) => {
    setSelectedDomains(selectedDomains.filter(d => d !== domain));
    setCompanies(companies.filter(c => c.domain !== domain));
  };

  const maxCII = Math.max(...companies.map(c => c.company_cii || 0), 1);
  const maxProducts = Math.max(...companies.map(c => c.product_count || 0), 1);

  return (
    <div className="space-y-6 animate-fade-in pb-8" data-testid="company-compare-page">
      {/* Back */}
      <button onClick={onBack} className="flex items-center gap-2 text-xs text-slate-400 hover:text-white transition-colors" data-testid="back-from-compare">
        <ArrowLeft className="w-4 h-4" /> Back to Companies
      </button>

      {/* Header */}
      <div className="rounded-2xl overflow-hidden" style={{ background: "linear-gradient(135deg, #0a0f1a 0%, #150f1a 100%)", border: "1px solid rgba(245,158,11,0.1)" }}>
        <div className="px-6 py-6">
          <h2 className="text-xl font-black text-white mb-1">Compare AI Companies</h2>
          <p className="text-xs text-slate-400">Side-by-side comparison of CII, orientation, products, and more</p>

          {/* Search to add */}
          <div className="mt-4 relative max-w-sm">
            <Search className="w-3.5 h-3.5 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={searchQuery}
              onChange={e => handleSearch(e.target.value)}
              placeholder={selectedDomains.length >= 4 ? "Max 4 companies" : "Add a company to compare..."}
              disabled={selectedDomains.length >= 4}
              className="w-full pl-8 pr-3 py-2 bg-white/[0.04] border border-white/[0.08] rounded-lg text-xs text-white placeholder:text-slate-500 focus:outline-none focus:border-amber-500/30 disabled:opacity-40"
              data-testid="compare-search"
            />
            {searchResults && searchResults.length > 0 && (
              <div className="absolute top-full mt-1 left-0 w-full bg-[#0c1222] border border-white/10 rounded-xl shadow-2xl z-50 max-h-48 overflow-y-auto">
                {searchResults.map(c => (
                  <div key={c.domain} onClick={() => addCompany(c.domain)} className="px-3 py-2 hover:bg-white/[0.04] cursor-pointer border-b border-white/[0.04] last:border-b-0 flex items-center gap-2">
                    <Plus className="w-3 h-3 text-amber-400 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs font-bold text-white truncate">{c.company_name}</p>
                      <p className="text-[10px] text-slate-500">{c.domain} &middot; CII: {c.company_cii}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Selected pills */}
          {selectedDomains.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3" data-testid="compare-selected-pills">
              {selectedDomains.map((d, i) => {
                const c = companies.find(co => co.domain === d);
                return (
                  <span key={d} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border" style={{ borderColor: `${DIM_COLORS[i]}40`, background: `${DIM_COLORS[i]}10`, color: DIM_COLORS[i] }}>
                    <Building2 className="w-3 h-3" />
                    {c?.company_name || d}
                    <button onClick={() => removeCompany(d)} className="ml-1 hover:opacity-70" data-testid={`compare-remove-${d}`}><X className="w-3 h-3" /></button>
                  </span>
                );
              })}
              <span className="text-[10px] text-slate-500 self-center">{selectedDomains.length}/4 selected · comparison auto-updates</span>
            </div>
          )}

          {/* Quick-pick chips for popular AI companies */}
          {selectedDomains.length < 4 && (
            <div className="mt-4">
              <div className="flex items-center gap-1.5 mb-2">
                <span className="text-[10px] uppercase tracking-wider text-amber-400 font-bold">Quick pick</span>
              </div>
              <div className="flex flex-wrap gap-2" data-testid="compare-quick-picks">
                {QUICK_PICK_COMPANIES.filter(c => !selectedDomains.includes(c.domain)).map(c => (
                  <button key={c.domain} onClick={() => addCompany(c.domain)}
                    data-testid={`compare-add-${c.domain}`}
                    className="text-[11px] px-3 py-1.5 bg-white/[0.03] border border-white/[0.08] rounded-full hover:border-amber-500/40 hover:text-amber-300 hover:bg-amber-500/[0.06] transition-all text-slate-300 inline-flex items-center gap-1">
                    <Plus className="w-2.5 h-2.5" /> {c.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Missing-domain warning — surfaced so user sees why fewer cards appear */}
      {missing.length > 0 && !loading && (
        <div className="px-4 py-3 bg-amber-500/10 border border-amber-500/30 rounded-lg flex items-start gap-2" data-testid="compare-missing-warning">
          <svg className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <div className="flex-1 text-[11px] text-amber-200/80">
            <span className="font-semibold">Not in our database yet:</span>{" "}
            <span className="font-mono">{missing.join(", ")}</span>.
            The comparison below reflects only the {companies.length} found compan{companies.length === 1 ? "y" : "ies"}.
            Help us by submitting these via the <span className="text-amber-100 underline decoration-dotted">Submit App</span> form.
          </div>
        </div>
      )}

      {/* Empty state */}
      {companies.length === 0 && !loading && (
        <div className="text-center py-16 bg-white/[0.01] border border-white/[0.04] rounded-2xl" data-testid="compare-empty-state">
          <Building2 className="w-10 h-10 text-slate-600 mx-auto mb-3" />
          <p className="text-sm text-slate-400 mb-1">Pick 2 or more companies to compare side-by-side</p>
          <p className="text-[11px] text-slate-500">Tap any quick-pick chip above, or search by name. Comparison generates automatically.</p>
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-16">
          <div className="w-10 h-10 rounded-full border-2 border-amber-500/30 border-t-amber-500 animate-spin" />
        </div>
      )}

      {/* Comparison Layout — Hero CII cards + Detailed Insights */}
      {companies.length > 0 && !loading && (
        <div className="space-y-6" data-testid="compare-results">
          {/* AI-generated insight summary (narrative + winners) */}
          {companies.length >= 2 && (
            <CompareInsights kind="company" entities={companies} />
          )}

          {/* Hero CII Cards Row */}
          <div className={`grid gap-4 ${companies.length === 2 ? "grid-cols-1 md:grid-cols-2" : companies.length === 3 ? "grid-cols-1 md:grid-cols-3" : "grid-cols-1 md:grid-cols-2 lg:grid-cols-4"}`}>
            {companies.map((c, i) => {
              const cii = Math.round(c.cii_score ?? c.company_cii ?? 0);
              const tone = cii >= 70 ? "#10b981" : cii >= 50 ? "#06b6d4" : cii >= 30 ? "#f59e0b" : "#ef4444";
              const dominantPct = Math.round(c.orientation_distribution?.[c.dominant_orientation] || 0);
              const founderLabel = (c.founder_composition || "unknown").replace(/_/g, " ");
              return (
                <div key={c.domain} className="relative rounded-2xl overflow-hidden border" style={{ borderColor: `${DIM_COLORS[i]}28`, background: `linear-gradient(135deg, ${DIM_COLORS[i]}10 0%, rgba(8,12,24,0.6) 100%)` }} data-testid={`hero-card-${c.domain}`}>
                  <div className="absolute top-0 left-0 w-full h-0.5" style={{ background: DIM_COLORS[i] }} />
                  <button onClick={() => onViewDetail?.(c.domain)} className="text-left w-full px-5 pt-5 hover:opacity-90 transition-opacity">
                    <p className="text-xl font-black text-white leading-tight">{c.company_name}</p>
                    <p className="text-[10px] text-slate-500 mt-0.5">{c.domain}</p>
                  </button>
                  <div className="px-5 pb-5 pt-4">
                    {/* Big CII */}
                    <div className="flex items-baseline gap-2 mb-3">
                      <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">CII Score</span>
                      <span className="text-4xl font-black tabular-nums" style={{ color: tone }}>{cii}</span>
                      <span className="text-xs text-slate-500">/100</span>
                    </div>
                    {/* Bar */}
                    <div className="h-1.5 bg-white/[0.04] rounded-full overflow-hidden mb-3">
                      <div className="h-full rounded-full transition-all duration-700" style={{ width: `${cii}%`, background: tone }} />
                    </div>
                    {/* Mini stats */}
                    <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t border-white/[0.05]">
                      <div>
                        <p className="text-[9px] uppercase tracking-wider text-slate-500 font-bold mb-1">Products</p>
                        <p className="text-lg font-black text-white tabular-nums">{c.product_count}</p>
                      </div>
                      <div>
                        <p className="text-[9px] uppercase tracking-wider text-slate-500 font-bold mb-1">Dominant</p>
                        <span className="inline-flex items-center gap-1.5 text-[10px] px-2 py-0.5 rounded-full font-bold" style={{ color: OC[c.dominant_orientation], background: `${OC[c.dominant_orientation]}18`, border: `1px solid ${OC[c.dominant_orientation]}30` }}>
                          {OC_LABELS[c.dominant_orientation]}{dominantPct ? ` ${dominantPct}%` : ""}
                        </span>
                      </div>
                      <div>
                        <p className="text-[9px] uppercase tracking-wider text-slate-500 font-bold mb-1">Country</p>
                        <p className="text-xs text-slate-200 truncate" title={c.country}>{c.country || "—"}</p>
                      </div>
                      <div>
                        <p className="text-[9px] uppercase tracking-wider text-slate-500 font-bold mb-1">Founders</p>
                        <p className="text-xs text-slate-200 capitalize truncate" title={founderLabel}>{founderLabel}</p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Insights / Highlights Strip */}
          {(() => {
            const cells = [];
            const ciiOf = (c) => c.cii_score ?? c.company_cii ?? 0;
            // Highest CII
            const topCii = [...companies].sort((a, b) => ciiOf(b) - ciiOf(a))[0];
            cells.push({ label: "Highest CII", value: topCii.company_name, sub: `${Math.round(ciiOf(topCii))}/100`, color: "#10b981", icon: Shield });
            // Most products
            const topProd = [...companies].sort((a, b) => (b.product_count || 0) - (a.product_count || 0))[0];
            cells.push({ label: "Most Products", value: topProd.company_name, sub: `${topProd.product_count} live`, color: "#3b82f6", icon: Package });
            // Most care-oriented
            const careOf = (c) => c.orientation_distribution?.care || 0;
            const topCare = [...companies].sort((a, b) => careOf(b) - careOf(a))[0];
            const topCareVal = Math.round(careOf(topCare));
            cells.push({ label: "Most Care-Oriented", value: topCareVal > 0 ? topCare.company_name : "None", sub: topCareVal > 0 ? `${topCareVal}% care` : "All utility/creative", color: "#10b981", icon: Building2 });
            // Most diverse founders
            const founderRank = { female_only: 3, mixed: 2, male_only: 1, unknown: 0 };
            const topFounder = [...companies].sort((a, b) => (founderRank[b.founder_composition] || 0) - (founderRank[a.founder_composition] || 0))[0];
            cells.push({ label: "Most Diverse Founders", value: topFounder.company_name, sub: (topFounder.founder_composition || "unknown").replace(/_/g, " "), color: "#a855f7", icon: Users });
            // Geographic spread
            const geoOf = (c) => Object.keys(c.country_distribution || {}).length;
            const topGeo = [...companies].sort((a, b) => geoOf(b) - geoOf(a))[0];
            cells.push({ label: "Widest Geographic Reach", value: topGeo.company_name, sub: `${geoOf(topGeo)} countries`, color: "#f59e0b", icon: Globe });
            return (
              <div className="bg-white/[0.02] border border-white/[0.06] rounded-2xl p-5" data-testid="compare-highlights">
                <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 mb-4">Comparison Highlights</h3>
                <div className={`grid gap-3 ${cells.length <= 3 ? "grid-cols-3" : "grid-cols-2 md:grid-cols-5"}`}>
                  {cells.map((cell) => {
                    const Icon = cell.icon;
                    return (
                      <div key={cell.label} className="rounded-xl p-3 bg-white/[0.02] border border-white/[0.04]">
                        <div className="flex items-center gap-1.5 mb-2">
                          <Icon className="w-3 h-3" style={{ color: cell.color }} />
                          <p className="text-[9px] uppercase tracking-wider text-slate-500 font-bold">{cell.label}</p>
                        </div>
                        <p className="text-xs font-black text-white leading-tight">{cell.value}</p>
                        <p className="text-[10px] mt-0.5" style={{ color: cell.color }}>{cell.sub}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Orientation Mix — visual bars */}
          <div className="bg-white/[0.02] border border-white/[0.06] rounded-2xl p-5" data-testid="compare-orientation">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Value Orientation Mix</h3>
              <div className="flex items-center gap-3 text-[10px]">
                {Object.entries(OC_LABELS).map(([k, label]) => (
                  <span key={k} className="inline-flex items-center gap-1">
                    <span className="w-2 h-2 rounded-sm" style={{ background: OC[k] }} /> {label}
                  </span>
                ))}
              </div>
            </div>
            <div className="space-y-3">
              {companies.map((c, i) => {
                const od = c.orientation_distribution || {};
                return (
                  <div key={c.domain} className="flex items-center gap-3">
                    <div className="w-32 flex-shrink-0">
                      <p className="text-xs font-bold truncate" style={{ color: DIM_COLORS[i] }}>{c.company_name}</p>
                    </div>
                    <div className="flex-1 h-6 rounded-md overflow-hidden flex bg-white/[0.03]">
                      {["care", "utility", "creative", "extraction"].map((k) => {
                        const v = od[k] || 0;
                        if (v === 0) return null;
                        return (
                          <div key={k} title={`${OC_LABELS[k]}: ${Math.round(v)}%`}
                            className="h-full flex items-center justify-center text-[10px] font-bold text-white/90 transition-all hover:brightness-125"
                            style={{ width: `${v}%`, background: OC[k] }}>
                            {v >= 12 ? `${Math.round(v)}%` : ""}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Industry Mix — bar chart per company */}
          {companies.some(c => c.industries && Object.keys(c.industries).length > 0) && (
            <div className="bg-white/[0.02] border border-white/[0.06] rounded-2xl p-5" data-testid="compare-industries">
              <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 mb-4">Industry Footprint</h3>
              <div className={`grid gap-4 ${companies.length <= 2 ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"}`}>
                {companies.map((c, i) => {
                  const ind = Object.entries(c.industries || {}).sort((a, b) => b[1] - a[1]).slice(0, 5);
                  const max = Math.max(...ind.map(([, v]) => v), 1);
                  return (
                    <div key={c.domain} className="bg-white/[0.02] border border-white/[0.04] rounded-xl p-4">
                      <p className="text-xs font-bold mb-3" style={{ color: DIM_COLORS[i] }}>{c.company_name}</p>
                      {ind.length === 0 ? (
                        <p className="text-xs text-slate-500 italic">No industry data</p>
                      ) : (
                        <div className="space-y-2">
                          {ind.map(([name, count]) => (
                            <div key={name}>
                              <div className="flex justify-between text-[10px] text-slate-400 mb-1">
                                <span className="truncate pr-2">{name}</span>
                                <span className="text-white font-bold tabular-nums">{count}</span>
                              </div>
                              <div className="h-1 bg-white/[0.04] rounded-full overflow-hidden">
                                <div className="h-full rounded-full" style={{ width: `${(count / max) * 100}%`, background: DIM_COLORS[i] }} />
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Product Gallery */}
          <div className="bg-white/[0.02] border border-white/[0.06] rounded-2xl p-5" data-testid="compare-products">
            <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 mb-4">Notable Products</h3>
            <div className={`grid gap-4 ${companies.length <= 2 ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"}`}>
              {companies.map((c, i) => {
                const products = (c.products || []).slice(0, 8);
                return (
                  <div key={c.domain} className="bg-white/[0.02] border border-white/[0.04] rounded-xl p-4">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-xs font-bold" style={{ color: DIM_COLORS[i] }}>{c.company_name}</p>
                      <p className="text-[10px] text-slate-500">{c.product_count} total</p>
                    </div>
                    {products.length === 0 ? (
                      <p className="text-xs text-slate-500 italic">No products listed</p>
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {products.map((p) => (
                          <span key={p} className="text-[10px] px-2.5 py-1 rounded-full bg-white/[0.04] border border-white/[0.06] text-slate-300">{p}</span>
                        ))}
                        {c.product_count > products.length && (
                          <span className="text-[10px] px-2.5 py-1 rounded-full bg-amber-500/[0.06] border border-amber-500/[0.18] text-amber-300">+{c.product_count - products.length} more</span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Detailed Stats Table */}
          <div className="bg-white/[0.02] border border-white/[0.06] rounded-2xl overflow-hidden" data-testid="compare-table">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/[0.06] bg-white/[0.02]">
                    <th className="px-5 py-3 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider w-44">Metric</th>
                    {companies.map((c, i) => (
                      <th key={c.domain} className="px-4 py-3 text-center min-w-[180px]">
                        <button onClick={() => onViewDetail?.(c.domain)} className="hover:opacity-80 transition-opacity">
                          <p className="text-sm font-black" style={{ color: DIM_COLORS[i] }}>{c.company_name}</p>
                        </button>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.03]">
                  <tr>
                    <td className="px-5 py-3 text-xs text-slate-400 font-medium">CII Score</td>
                    {companies.map((c, i) => {
                      const cii = Math.round(c.cii_score ?? c.company_cii ?? 0);
                      return (
                        <td key={c.domain} className="px-4 py-3 text-center">
                          <span className="text-base font-black tabular-nums" style={{ color: DIM_COLORS[i] }}>{cii}</span>
                          <span className="text-[10px] text-slate-500 ml-1">/100</span>
                        </td>
                      );
                    })}
                  </tr>
                  <tr>
                    <td className="px-5 py-3 text-xs text-slate-400 font-medium">Software / Hardware</td>
                    {companies.map(c => (
                      <td key={c.domain} className="px-4 py-3 text-center text-xs">
                        <span className="text-slate-200">{c.software_count || 0}</span>
                        <span className="text-slate-500 mx-1">/</span>
                        <span className="text-slate-200">{c.hardware_count || 0}</span>
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td className="px-5 py-3 text-xs text-slate-400 font-medium">Geographic Spread</td>
                    {companies.map(c => {
                      const cd = c.country_distribution || {};
                      const top = Object.entries(cd).sort((a, b) => b[1] - a[1]).slice(0, 3);
                      return (
                        <td key={c.domain} className="px-4 py-3 text-center text-[10px] text-slate-300">
                          {top.length === 0 ? "—" : top.map(([country, count]) => `${country} (${count})`).join(", ")}
                          {Object.keys(cd).length > 3 && <span className="text-slate-500"> +{Object.keys(cd).length - 3}</span>}
                        </td>
                      );
                    })}
                  </tr>
                  <tr>
                    <td className="px-5 py-3 text-xs text-slate-400 font-medium">Industries Covered</td>
                    {companies.map(c => (
                      <td key={c.domain} className="px-4 py-3 text-center text-xs text-slate-300 tabular-nums">{Object.keys(c.industries || {}).length}</td>
                    ))}
                  </tr>
                  <tr>
                    <td className="px-5 py-3 text-xs text-slate-400 font-medium">Headquarters</td>
                    {companies.map(c => (
                      <td key={c.domain} className="px-4 py-3 text-center text-xs text-slate-300">{c.country || "—"}</td>
                    ))}
                  </tr>
                  <tr>
                    <td className="px-5 py-3 text-xs text-slate-400 font-medium">Founder Composition</td>
                    {companies.map(c => (
                      <td key={c.domain} className="px-4 py-3 text-center text-xs text-slate-300 capitalize">{(c.founder_composition || "unknown").replace(/_/g, " ")}</td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyCompare;
